/**
 * Supabase Storage helper — 총판 공유 파일(distributor-assets) 업/다운로드.
 * Import from "@/lib/storage".
 *
 * Backend-less: 브라우저가 anon key 로 프라이빗 버킷에 직접 업/다운로드하고,
 * RLS(버킷 정책) + Cloudflare Access 가 접근을 통제한다. service_role 금지.
 * 다운로드는 signed URL(만료) 사용. 대용량/외부호스팅 자산은 external_url 대안.
 *
 * ENV-SAFE: 모든 호출이 getSupabase()/isSupabaseConfigured() 가드를 거친다.
 */

import { getSupabase, isSupabaseConfigured } from "@/lib/supabase";

export { isSupabaseConfigured };

/** 프라이빗 버킷 이름. (Supabase 콘솔/마이그레이션에서 생성 — Do 승인 단계에서 준비.) */
export const DISTRIBUTOR_BUCKET = "distributor-assets";

/** object key 규약: distributor-assets/{country|_all}/{assetId}/{version}/{fileName}. */
export function assetObjectPath(
  assetId: string,
  version: number,
  fileName: string,
  countryId?: string | null,
): string {
  const country = countryId && countryId.trim() ? countryId : "_all";
  const safeName = fileName.replace(/[^\w.\-]+/g, "_");
  return `${country}/${assetId}/${version}/${safeName}`;
}

function assertConfigured(): void {
  if (!isSupabaseConfigured()) {
    throw new Error(
      "[storage] Supabase is not configured. Set VITE_SUPABASE_URL / " +
        "VITE_SUPABASE_ANON_KEY, or guard UI with isSupabaseConfigured().",
    );
  }
}

/** 파일 업로드 (버전당 1회, 덮어쓰기 금지 — 버전 교체는 새 object key). */
export async function uploadAssetFile(objectPath: string, file: File | Blob): Promise<void> {
  assertConfigured();
  const { error } = await getSupabase()
    .storage.from(DISTRIBUTOR_BUCKET)
    .upload(objectPath, file, { upsert: false, contentType: (file as File).type || undefined });
  if (error) throw new Error(`[storage] upload failed: ${error.message}`);
}

/** 다운로드용 signed URL (기본 1시간 만료). */
export async function signedAssetUrl(objectPath: string, expiresInSec = 3600): Promise<string> {
  assertConfigured();
  const { data, error } = await getSupabase()
    .storage.from(DISTRIBUTOR_BUCKET)
    .createSignedUrl(objectPath, expiresInSec);
  if (error || !data) throw new Error(`[storage] signed url failed: ${error?.message ?? "no data"}`);
  return data.signedUrl;
}

/** object 삭제 (관리자 정리용 — 제공 이력이 참조하는 버전은 삭제 금지 권장). */
export async function removeAssetFile(objectPath: string): Promise<void> {
  assertConfigured();
  const { error } = await getSupabase().storage.from(DISTRIBUTOR_BUCKET).remove([objectPath]);
  if (error) throw new Error(`[storage] remove failed: ${error.message}`);
}
